import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, Search, Users, BarChart3, FileText } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

type AnalysisStage = 'pending' | 'competitors' | 'profiles' | 'employees' | 'generating' | 'completed' | 'failed';

const SocialSellingStatus = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<AnalysisStage>('pending');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [startTime] = useState(Date.now());
  const [showViewReportButton, setShowViewReportButton] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/raport-socialselling');
      return;
    }

    let intervalId: ReturnType<typeof setInterval>;
    let progressInterval: ReturnType<typeof setInterval>;

    const checkStatus = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-audit-status', {
          body: { id }
        });

        if (error) throw error;

        const submission = data?.data;
        
        if (!submission) {
          setError('Nie znaleziono zgłoszenia');
          return;
        }

        const currentStatus = submission.status;
        const elapsedTime = Date.now() - startTime;

        // If stuck in 'processing' for over 3 minutes, show "View Report Anyway" button
        if (currentStatus === 'processing' && elapsedTime > 3 * 60 * 1000) {
          setShowViewReportButton(true);
        }

        // If stuck in 'processing' for over 5 minutes but has report_data, auto-navigate
        if (currentStatus === 'processing' && elapsedTime > 5 * 60 * 1000 && submission.report_data) {
          console.log('⚠️ Audit stuck in processing but has report_data, navigating anyway...');
          setStatus('completed');
          setProgress(100);
          clearInterval(intervalId);
          clearInterval(progressInterval);
          setTimeout(() => navigate(`/raport-socialselling/raport/${id}`), 500);
          return;
        }

        if (currentStatus === 'completed' && submission.report_data) {
          setStatus('completed');
          setProgress(100);
          clearInterval(intervalId);
          clearInterval(progressInterval);
          setTimeout(() => navigate(`/raport-socialselling/raport/${id}`), 1500);
        } else if (currentStatus === 'failed') {
          setStatus('failed');
          setError('Wystąpił błąd podczas analizy. Spróbuj ponownie.');
          clearInterval(intervalId);
          clearInterval(progressInterval);
        } else if (currentStatus === 'processing') {
          // Estimate stage based on time elapsed
          const createdAt = new Date(submission.created_at).getTime();
          const now = Date.now();
          const elapsed = now - createdAt;
          
          if (elapsed < 30000) {
            setStatus('competitors');
          } else if (elapsed < 60000) {
            setStatus('profiles');
          } else if (elapsed < 90000) {
            setStatus('employees');
          } else {
            setStatus('generating');
          }
        }
      } catch (err: any) {
        console.error('Status check error:', err);
        setError(err.message || 'Wystąpił błąd');
      }
    };

    // Trigger the edge function on mount
    const startAudit = async () => {
      try {
        await supabase.functions.invoke('audit-social-selling', {
          body: { submissionId: id }
        });
      } catch (err) {
        console.error('Failed to start audit:', err);
      }
    };

    startAudit();
    checkStatus();
    intervalId = setInterval(checkStatus, 3000); // Poll every 3 seconds

    // Smooth progress animation
    progressInterval = setInterval(() => {
      setProgress(prev => {
        if (status === 'completed') return 100;
        const target = getTargetProgress(status);
        return prev < target ? Math.min(prev + 1, target) : prev;
      });
    }, 200);

    return () => {
      clearInterval(intervalId);
      clearInterval(progressInterval);
    };
  }, [id, navigate, status]);

  const getTargetProgress = (stage: AnalysisStage): number => {
    switch (stage) {
      case 'pending': return 10;
      case 'competitors': return 25;
      case 'profiles': return 50;
      case 'employees': return 70;
      case 'generating': return 90;
      case 'completed': return 100;
      default: return 0;
    }
  };

  const stages = [
    { id: 'competitors', icon: Search, label: 'Zbieranie danych o konkurencji', color: 'text-blue-500' },
    { id: 'profiles', icon: BarChart3, label: 'Analiza profili LinkedIn', color: 'text-purple-500' },
    { id: 'employees', icon: Users, label: 'Analiza pracowników', color: 'text-green-500' },
    { id: 'generating', icon: FileText, label: 'Generowanie raportu AI', color: 'text-orange-500' },
  ];

  const getStageStatus = (stageId: string) => {
    const stageOrder = ['competitors', 'profiles', 'employees', 'generating'];
    const currentIndex = stageOrder.indexOf(status);
    const stageIndex = stageOrder.indexOf(stageId);
    
    if (currentIndex > stageIndex) return 'completed';
    if (currentIndex === stageIndex) return 'active';
    return 'pending';
  };

  if (error) {
    return (
      <ScrollArea className="h-screen w-full">
        <div className="min-h-screen gradient-subtle flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-destructive">Wystąpił błąd</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">{error}</p>
              <button
                onClick={() => navigate('/raport-socialselling')}
                className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Wróć do formularza
              </button>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="h-screen w-full">
      <div className="min-h-screen gradient-subtle py-16">
        <div className="container px-4">
          <div className="max-w-2xl mx-auto">
            
            <Card className="border-none shadow-2xl">
              <CardHeader className="text-center pb-6">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4 mx-auto animate-pulse">
                  <Loader2 className="h-10 w-10 text-primary animate-spin" />
                </div>
                <CardTitle className="text-3xl mb-2">
                  Analizujemy Twoją firmę
                </CardTitle>
                <p className="text-muted-foreground">
                  To potrwa około 2-3 minut. Nie zamykaj tej strony.
                </p>
              </CardHeader>

              <CardContent className="space-y-8">
                
                {/* Overall Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Postęp</span>
                    <span className="font-semibold text-primary">{Math.round(progress)}%</span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </div>

                {/* Stages */}
                <div className="space-y-4">
                  {stages.map((stage, index) => {
                    const stageStatus = getStageStatus(stage.id);
                    const Icon = stage.icon;
                    
                    return (
                      <div
                        key={stage.id}
                        className={`flex items-start gap-4 p-4 rounded-lg transition-all ${
                          stageStatus === 'active' 
                            ? 'bg-primary/5 border-2 border-primary/20' 
                            : stageStatus === 'completed'
                            ? 'bg-secondary/30'
                            : 'bg-secondary/10'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {stageStatus === 'completed' ? (
                            <CheckCircle2 className="h-6 w-6 text-green-500" />
                          ) : stageStatus === 'active' ? (
                            <Icon className={`h-6 w-6 ${stage.color} animate-pulse`} />
                          ) : (
                            <Icon className="h-6 w-6 text-muted-foreground/40" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${
                            stageStatus === 'active' ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {stage.label}
                          </p>
                          {stageStatus === 'active' && (
                            <p className="text-sm text-muted-foreground mt-1">
                              W trakcie...
                            </p>
                          )}
                          {stageStatus === 'completed' && (
                            <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                              Ukończono
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Completion Message */}
                {status === 'completed' && (
                  <div className="text-center p-6 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800 animate-fade-in">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-green-700 dark:text-green-400">
                      Analiza zakończona!
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                      Przekierowujemy do raportu...
                    </p>
                  </div>
                )}

                {/* Estimated Time */}
                {status !== 'completed' && !showViewReportButton && (
                  <div className="text-center text-sm text-muted-foreground">
                    Szacowany czas pozostały: {Math.max(0, 3 - Math.floor(progress / 33))} min
                  </div>
                )}

                {/* View Report Anyway Button */}
                {showViewReportButton && status !== 'completed' && (
                  <div className="text-center space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Analiza trwa dłużej niż zwykle...
                    </p>
                    <button
                      onClick={() => navigate(`/raport-socialselling/raport/${id}`)}
                      className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                    >
                      Zobacz raport
                    </button>
                  </div>
                )}

              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </ScrollArea>
  );
};

export default SocialSellingStatus;
